import React from 'react';
import { StandardTableActions } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { SupplierInvoicePayment } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';

const formatCurrency = (amount?: number | null) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

export const moduleConfig = {
  module: {
    title: 'Supplier Invoice Payments',
    description: 'Manage supplier invoice payments',
    permissionModuleKey: 'supplier_payments',
    route: {
      list: '/supplier-invoice-payments',
      new: '/supplier-invoice-payments/new',
      detail: (id: number) => `/supplier-invoice-payments/${id}`,
      edit: (id: number) => `/supplier-invoice-payments/${id}/edit`,
    },
    api: {
      list: '/supplier-invoice-payments',
      detail: (id: number) => `/supplier-invoice-payments/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search by invoice number, supplier, amount...',
  },

  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['invoiceNumber', 'supplierName', 'paymentDate', 'paymentAmount'] },
    { name: 'advanced', label: 'Advanced', icon: '⚙️', fields: ['paymentNotes'] },
  ],

  filters: {
    fields: [
      { key: 'invoiceNumber', label: 'Invoice No', placeholder: 'All', getValue: (item: SupplierInvoicePayment) => item.invoice?.invoiceNo || '', options: [] },
      { key: 'supplierName', label: 'Supplier', placeholder: 'All', getValue: (item: SupplierInvoicePayment) => item.invoice?.supplier?.name || '', options: [], apiEndpoint: '/suppliers', labelField: 'name' },
      { key: 'paymentDate', label: 'Payment Date', type: 'date' as const, getValue: (item: SupplierInvoicePayment) => item.paymentDate ? formatDateGlobal(item.paymentDate) : undefined, options: [] },
      { key: 'paymentAmount', label: 'Amount', type: 'number' as const, getValue: (item: SupplierInvoicePayment) => item.paymentAmount?.toString() ?? '', options: [] },
      { key: 'paymentNotes', label: 'Notes', placeholder: 'All', getValue: (item: SupplierInvoicePayment) => item.paymentNotes || '', options: [] },
    ],
    applyFilter: (
      items: SupplierInvoicePayment[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ): SupplierInvoicePayment[] => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        const str = (v: unknown) => (v == null ? '' : String(v)).toLowerCase();
        if (Array.isArray(filters.invoiceNumber) && filters.invoiceNumber.length && !filters.invoiceNumber.some((f) => str(item.invoice?.invoiceNo).includes(str(f)))) return false;
        if (Array.isArray(filters.supplierName) && filters.supplierName.length && !filters.supplierName.some((f) => str(item.invoice?.supplier?.name).includes(str(f)))) return false;
        if (Array.isArray(filters.paymentDate) && filters.paymentDate.length && item.paymentDate && ranges.paymentDate?.start && ranges.paymentDate?.end) {
          const d = new Date(item.paymentDate);
          if (d < ranges.paymentDate.start || d > ranges.paymentDate.end) return false;
        }
        if (Array.isArray(filters.paymentAmount) && filters.paymentAmount.length && !filters.paymentAmount.includes(String(item.paymentAmount))) return false;
        if (typeof filters.paymentNotes === 'string' && filters.paymentNotes.trim() && !str(item.paymentNotes).includes(str(filters.paymentNotes))) return false;
        return true;
      });
    },
  },

  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'Add Payment', showTitle: false, variant: 'primary' as const, href: '/supplier-invoice-payments/new' },
    ],
  },

  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: SupplierInvoicePayment) => { window.location.href = `/supplier-invoice-payments/${item.id}`; },
    keyExtractor: (item: SupplierInvoicePayment) => item.id,
  },

  emptyStates: {
    noData: 'Get started by creating a new supplier invoice payment.',
    noSearchResults: 'No payments found.',
    noFilterResults: 'No payments match the selected filters.',
  },

  delete: {
    title: 'Delete Payment',
    message: 'Are you sure you want to delete this payment? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete payment.',
    handler: async (id: number) => {
      const { deleteSupplierInvoicePayment } = await import('../../services/mutations');
      await deleteSupplierInvoicePayment(id);
    },
  },

  utils: {
    formatDate: (dateString?: string | null) => (dateString ? formatDateGlobal(dateString) : '-'),
    formatCurrency,
  },

  detail: {
    subtitle: 'Payment Details',
    notFoundTitle: 'Payment not found',
    notFoundMessage: "The payment you're looking for doesn't exist.",
    notFoundBackText: 'Back to Supplier Payments',
    defaultTitle: 'Payment',
    getTitle: (item: SupplierInvoicePayment) =>
      item.invoice?.invoiceNo ? `Payment for ${item.invoice.invoiceNo}` : `Payment #${item.id}`,
  },

  form: {
    new: { title: 'New Supplier Payment', subtitle: 'Add a new supplier invoice payment', submitText: 'Create Payment' },
    edit: { title: 'Edit Payment', subtitle: 'Update payment', submitText: 'Update Payment' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Failed to save payment.',
    create: async (data: Partial<SupplierInvoicePayment>) => {
      const { createSupplierInvoicePayment } = await import('../../services/mutations');
      return await createSupplierInvoicePayment(data);
    },
    update: async (id: number, data: Partial<SupplierInvoicePayment>) => {
      const { updateSupplierInvoicePayment } = await import('../../services/mutations');
      return await updateSupplierInvoicePayment(id, data);
    },
  },

  formLayout: {
    mode: 'default' as const,
    sections: [
      { id: 'payment', title: 'Payment Information', icon: '💳', gridCols: 'md:grid-cols-2', fields: ['supplierInvoiceId', 'paymentDate', 'paymentAmount', 'paymentNotes'], collapsible: false },
    ],
  },

  fields: [
    { name: 'supplierInvoiceId', label: 'Supplier Invoice', type: 'select', required: true, placeholder: 'Select invoice', apiEndpoint: '/supplier-invoices', labelField: 'invoiceNo', valueField: 'id' },
    { name: 'paymentDate', label: 'Payment Date', type: 'date', placeholder: 'Date' },
    { name: 'paymentAmount', label: 'Amount', type: 'number', required: true, placeholder: '0' },
    { name: 'paymentNotes', label: 'Notes', type: 'textarea', placeholder: 'Notes' },
  ],
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SupplierInvoicePayment>[] => [
  { key: 'invoice.invoiceNo', header: 'Invoice No.', sortable: true, render: (_, row) => row.invoice?.invoiceNo || '-' },
  { key: 'invoice.supplier.name', header: 'Supplier', sortable: true, render: (_, row) => <TruncatedText text={row.invoice?.supplier?.name || '-'} maxLength={40} showTooltip /> },
  { key: 'paymentDate', header: 'Payment Date', sortable: true, render: (_, row) => formatDate(row.paymentDate) },
  { key: 'paymentAmount', header: 'Amount', sortable: true, render: (_, row) => formatCurrency(row.paymentAmount) },
  { key: 'paymentNotes', header: 'Notes', sortable: false, render: (v) => (v ? <TruncatedText text={String(v)} maxLength={30} showTooltip /> : '-') },
];

export const getDetailSections = (item: SupplierInvoicePayment, formatDate: (dateString?: string) => string) => [
  {
    title: 'Payment Information',
    fields: [
      { label: 'Invoice', value: item.invoice?.invoiceNo ? `#${item.invoice.id} - ${item.invoice.invoiceNo}` : `#${item.supplierInvoiceId}` },
      { label: 'Supplier', value: item.invoice?.supplier?.name || '-' },
      { label: 'Payment Date', value: formatDate(item.paymentDate) },
      { label: 'Amount', value: formatCurrency(item.paymentAmount) },
      { label: 'Notes', value: item.paymentNotes || '-' },
    ],
  },
];

export const getStatusBadges = (_item: SupplierInvoicePayment): React.ReactNode => null;

export const getInitialFormData = (): Partial<SupplierInvoicePayment> => ({
  supplierInvoiceId: undefined as unknown as number,
  paymentDate: undefined,
  paymentAmount: undefined as unknown as number,
  paymentNotes: '',
});

export const mapItemToFormData = (item: SupplierInvoicePayment): Partial<SupplierInvoicePayment> => ({
  supplierInvoiceId: item.supplierInvoiceId,
  paymentDate: item.paymentDate,
  paymentAmount: item.paymentAmount,
  paymentNotes: item.paymentNotes || '',
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

export const getTableActions = (
  item: SupplierInvoicePayment,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

